import { cx } from '@linaria/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { userPreferencesQuery } from '@pm2/api';
import { getAllCoursesQuery } from '@pm2/course-builder';
import { useAppContext } from '@provider/app-context';
import { InMemoryNavigationProvider, useLocation } from '@provider/navigation';
import { useUserIdentityState } from '@provider/user-identity';
import { resets } from '@ui/css';
import { FocusOn, FloatingActionButton, CrossIcon } from '@ui/kit';
import { isPlatformNodeJS, prefetchQueryIfNeeded } from '@utils/common';
import { getSubjectsQuery } from '@utils/constants';
import { useMatchBreakpoint, useChangeEffect, SizeTransition, useSelf } from '@utils/react';
import { focusOnCls, contentCls, backdropCls, mobileContentWrapperCls, wrapperCls, userAreaCls, closeButtonCls } from './comp.user-area.style.js';
import { userAreaBreakpoints } from './const.breakpoints.js';
import { Routes } from './const.routes.js';
import { getPortalsForUserQuery } from './features/portals/api.get-for-user.js';
import { getTeamsQuery } from './features/settings/molecule.my-teams/api.get-teams.query.js';
import { getStatusForProductQuery } from './features/status/api.get-status.js';
import { Content } from './layout/content.js';
import { USERAREA_EXIT_DURATION_MS, userAreaTransitionCls, USERAREA_ENTER_DURATION_MS, BUTTON_FADE_IN_DURATION_MS } from './transition.user-area.style.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { LeftNav } from './layout/left-nav/root.left-nav.js';
import { DEFAULT_TIMESPAN } from './features/component-entries/utils/timespan.js';
import { componentEntryStore } from './features/component-entries/state.js';
import { getNotificationsQuery } from './features/notifications/api.get-notifications.query.js';
import { getComponentEntriesQuery } from './features/component-entries/api/query.get-component-entries.js';

const UserArea = props => {
  const isMobile = useMatchBreakpoint(userAreaBreakpoints.mobile);
  usePrefetchedData(props);

  // Ensure that componentEntry filters are reset once the user leaves the
  // user-area.
  useChangeEffect(() => {
    if (!props.active) {
      const timeoutId = setTimeout(() => {
        componentEntryStore.dispatch(componentEntryStore.actions.reset());
      }, USERAREA_EXIT_DURATION_MS);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [props.active]);
  return /*#__PURE__*/jsx(TransitionGroup, {
    component: null,
    children: props.active && /*#__PURE__*/jsx(SizeTransition, {
      timeout: {
        enter: USERAREA_ENTER_DURATION_MS + BUTTON_FADE_IN_DURATION_MS,
        exit: USERAREA_EXIT_DURATION_MS
      },
      axis: "vertical",
      classNames: userAreaTransitionCls,
      children: ref => /*#__PURE__*/jsx(InMemoryNavigationProvider, {
        basePath: isPlatformNodeJS() ? "/" : window.location.pathname,
        initialPath: getInitialRoute(isMobile ?? false),
        children: /*#__PURE__*/jsx(FocusOn, {
          className: focusOnCls,
          onEscapeKey: props.onClose,
          onClickOutside: props.onClose,
          scrollLock: true,
          focusLock: true,
          allowPinchZoom: true,
          autoFocus: true,
          returnFocus: {
            preventScroll: true
          },
          preventScrollOnFocus: true,
          children: /*#__PURE__*/jsx(UserAreaContent, {
            ...props,
            ref: ref
          })
        })
      })
    })
  });
};
function UserAreaContent(props) {
  const contentRef = useRef(null);
  const isMobile = useMatchBreakpoint(userAreaBreakpoints.mobile);
  const location = useLocation();
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx("article", {
      ref: props.ref,
      className: cx(resets.rootCls, userAreaCls),
      children: /*#__PURE__*/jsx("div", {
        className: cx(wrapperCls, location.pathname !== "/" && "atContent"),
        "data-is-wrapper": true,
        "data-user-area-wrapper": true,
        children: /*#__PURE__*/jsx("div", {
          className: cx(mobileContentWrapperCls, location.pathname !== "/" && "atContent"),
          children: /*#__PURE__*/jsxs("div", {
            className: contentCls,
            ref: contentRef,
            "data-is-content": true,
            children: [/*#__PURE__*/jsx(LeftNav, {}), /*#__PURE__*/jsx(Content, {}), !isMobile && /*#__PURE__*/jsx("i", {
              role: "presentation",
              className: backdropCls,
              children: props.backdropIllustration
            })]
          })
        })
      })
    }), /*#__PURE__*/jsx("div", {
      "data-is-button": true,
      className: cx(closeButtonCls, props.active && "active"),
      children: /*#__PURE__*/jsx(FloatingActionButton, {
        onClick: props.onClose,
        tooltip: {
          placement: "right",
          body: "Luk",
          ariaType: "label"
        },
        children: /*#__PURE__*/jsx(FloatingActionButton.Icon, {
          children: /*#__PURE__*/jsx(CrossIcon, {
            color: "currentColor",
            height: "22.5"
          })
        })
      })
    })]
  });
}
/**
 * Fetches and prepares data required by the UserArea on demand
 */
function usePrefetchedData({
  requestedActive,
  onReady,
  onUrgentNotifications
}) {
  const productId = useAppContext(state => state.product?.isbn);
  const user = useUserIdentityState(state => state.user);
  const self = useSelf({});
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!user) {
      return;
    }

    // Start fetching data as soon as the component mounts!
    const statusPromise = queryClient.fetchQuery(getStatusForProductQuery(productId));
    statusPromise.then(statusEntries => {
      const urgentNotifications = statusEntries.filter(it => it.urgent);
      if (onUrgentNotifications && urgentNotifications.length) {
        onUrgentNotifications(urgentNotifications);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);
  useEffect(() => {
    // In case we have not requested the userArea yet or if we are already
    // fetching the data required to render the user area then bail out!
    if (!requestedActive || self.hasInitialisedDataFetching || !user) {
      return;
    }
    self.hasInitialisedDataFetching = true;
    const initialRoute = getInitialRoute(matchMedia(userAreaBreakpoints.mobile.toQuery()).matches);
    const allQueryPromises = {
      subjects: prefetchQueryIfNeeded(queryClient, getSubjectsQuery()),
      portals: prefetchQueryIfNeeded(queryClient, getPortalsForUserQuery(user)),
      courses: prefetchQueryIfNeeded(queryClient, getAllCoursesQuery(user)),
      status: prefetchQueryIfNeeded(queryClient, getStatusForProductQuery(productId)),
      componentEntries: prefetchQueryIfNeeded(queryClient, getComponentEntriesQuery(user, DEFAULT_TIMESPAN)),
      notifications: prefetchQueryIfNeeded(queryClient, getNotificationsQuery(user)),
      teams: prefetchQueryIfNeeded(queryClient, getTeamsQuery(user)),
      preferences: prefetchQueryIfNeeded(queryClient, userPreferencesQuery.queryConfig({
        userId: user.id
      }))
    };
    const requiredQueryPromises = [];
    switch (initialRoute) {
      case Routes.COURSES:
        requiredQueryPromises.push(allQueryPromises.courses, allQueryPromises.notifications);
        break;
      case Routes.ENTRIES:
        requiredQueryPromises.push(allQueryPromises.componentEntries, allQueryPromises.notifications, allQueryPromises.teams);
        break;
      case Routes.PORTALS:
        requiredQueryPromises.push(allQueryPromises.portals);
        break;
      case Routes.TEAMS:
        requiredQueryPromises.push(allQueryPromises.teams);
        break;
      case Routes.FONT:
      case Routes.SPEECH:
        requiredQueryPromises.push(allQueryPromises.preferences);
        break;
      case Routes.STATUS:
        requiredQueryPromises.push(allQueryPromises.status);
        break;
    }

    // Once we've loaded all data required to render to UserArea let the host
    // know!
    Promise.allSettled(requiredQueryPromises).then(() => {
      onReady?.();
    }).catch(err => {
      console.error("UserArea(): failed to fetch data", err);
      onReady?.();
    });
  }, [onReady, productId, queryClient, requestedActive, self, user]);
}
function getInitialRoute(isMobile) {
  const fallbackRoute = isMobile ? "/" : Routes.ENTRIES;
  if (isPlatformNodeJS()) {
    return fallbackRoute;
  }
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get("dashboard") || fallbackRoute;
}

export { UserArea };
//# sourceMappingURL=comp.user-area.js.map
